import { sleep } from "./sleep"

export interface UploadRetryOptions {
    retries?: number
    baseDelayMs?: number
    maxDelayMs?: number
    shouldRetry?: (error: unknown, attempt: number) => boolean
    onRetry?: (error: unknown, attempt: number, delayMs: number) => void
}

const DEFAULT_RETRIES = 3
const DEFAULT_BASE_DELAY_MS = 500
const DEFAULT_MAX_DELAY_MS = 4000

// モバイル回線切断でバックエンドのS3アップロードが途中で切れる事象の緩和策。
// 4xx（認証エラー等の恒久的失敗）はリトライしても結果が変わらないため対象外。
// AbortError（撮影中の × による中断）も意図的なキャンセルなのでリトライしない。
export const isRetryableUploadError = (error: unknown): boolean => {
    if (error && typeof error === "object" && "name" in error) {
        if (error.name === "AbortError") return false
    }
    if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        typeof error.status === "number"
    ) {
        const status = error.status
        if (status >= 400 && status < 500) return false
    }
    return true
}

export const uploadWithRetry = async <T>(
    fn: (attempt: number) => Promise<T>,
    options: UploadRetryOptions = {}
): Promise<T> => {
    const {
        retries = DEFAULT_RETRIES,
        baseDelayMs = DEFAULT_BASE_DELAY_MS,
        maxDelayMs = DEFAULT_MAX_DELAY_MS,
        shouldRetry = isRetryableUploadError,
        onRetry
    } = options

    const normalizedRetries = Number.isFinite(retries)
        ? Math.max(0, Math.floor(retries))
        : DEFAULT_RETRIES

    let lastError: unknown
    for (let attempt = 0; attempt <= normalizedRetries; attempt++) {
        try {
            return await fn(attempt)
        } catch (error) {
            lastError = error
            if (attempt === normalizedRetries || !shouldRetry(error, attempt)) {
                throw error
            }
            const backoff = Math.min(baseDelayMs * 2 ** attempt, maxDelayMs)
            const jitter = Math.random() * backoff * 0.3
            const delayMs = Math.round(backoff + jitter)
            onRetry?.(error, attempt, delayMs)
            await sleep(delayMs)
        }
    }
    throw lastError
}
